// Copyright ©️ 2026 eVolve MEP, LLC

import type { Shipment } from '@evolvemep/api-ts';
import { Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { GridMenu } from 'components/Mantine/GridMenu';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { ShipmentPackingSlipModal } from 'modules/Shop/Shipments/ShipmentDetail/ShipmentPackingSlipModal';
import { useCanEditShipment } from 'modules/Shop/Shipments/ShipmentDetail/useCanEditShipment';
import { useShipments } from 'modules/Shop/Shipments/ShipmentsPage/useShipments';

type ShipmentGridMenuProps = {
  shipment: Shipment;
  onShipmentUpdated: () => void;
  onShipmentDeleted: () => void;
};
export const ShipmentWorkflowGridMenu = ({ shipment, onShipmentUpdated, onShipmentDeleted }: ShipmentGridMenuProps) => {
  const { canEditShipment } = useCanEditShipment(shipment);
  const shipmentsContext = useShipments();
  const { mutationRunning, deleteShipment } = shipmentsContext;
  return (
    <GridMenu
      variant="light"
      size="input-sm"
      options={[
        {
          label: 'Revert to draft',
          value: 'revertToDraft',
          hidden: !canEditShipment('revertToDraft'),
        },
        {
          label: 'Revert to ready to ship',
          value: 'revertToReady',
          hidden: !canEditShipment('revertToReady'),
        },
        {
          label: 'Revert to shipped',
          value: 'revertToShipped',
          hidden: !canEditShipment('revertToShipped'),
        },
        {
          label: 'Print packing slip',
          value: 'printPackingSlip',
        },
        {
          label: 'Delete',
          value: 'deleteShipment',
          hidden: !canEditShipment('delete'),
          color: 'red',
        },
      ]}
      disabled={mutationRunning}
      onMenuItemSelected={async (item) => {
        // Both are handled by the modals below; everything else maps to a workflow mutation on the context.
        if (item === 'deleteShipment' || item === 'printPackingSlip') return false;
        const action = shipmentsContext[item];
        await action(shipment.shipmentId);
        onShipmentUpdated();
        return true;
      }}
    >
      {(menuItemSelected, reset) => (
        <>
          <ShipmentPackingSlipModal
            shipment={shipment}
            opened={menuItemSelected === 'printPackingSlip'}
            onClose={reset}
          />
          <WrappedModal
            title="Delete shipping order"
            opened={menuItemSelected === 'deleteShipment'}
            onClose={reset}
            confirmationText="Delete"
            onConfirm={async () => {
              await deleteShipment(shipment.shipmentId);
              notifications.show({
                title: 'Successfully deleted shipping order',
                message: `Shipping order ${shipment.name} deleted.`,
                color: 'green',
              });
              onShipmentDeleted();
            }}
          >
            <Text>
              Are you sure you want to delete shipping order <b>{shipment.name}</b>? All associated work order items
              will be removed from this shipping order. This action cannot be undone.
            </Text>
          </WrappedModal>
        </>
      )}
    </GridMenu>
  );
};
